/**
 * Display formatters for the accounts-payable console.
 *
 * Kept local rather than imported from `consulting-engagements`: this surface is
 * platform-wide and mostly renders consultation rows, so borrowing the
 * engagement Hub's prototype formatters would couple an AP tool to a product
 * feature's display decisions.
 */
import { Money } from '@/core/money';

/**
 * Render a `Money` off the wire.
 *
 * `cents` is a `String!` at the schema — a JS number cannot hold every int64 the
 * api can send — so it is parsed here at the display boundary and nowhere else.
 * Currency always accompanies the amount; there is no default, because guessing
 * USD on an AP screen is how a EUR payout gets read as dollars.
 */
export function formatMoney(amount: { cents: string; currency: string }): string {
  return new Money(amount.cents, amount.currency).formatted();
}

/** Minor units as a number, for comparison and summing WITHIN one currency. */
export function centsOf(amount: { cents: string }): number {
  return Number.parseInt(amount.cents, 10);
}

/** "Apr 1, 2026" */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Apr 1, 2026, 2:30 PM" — full precision, for the audit fields. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The tax year a `paidAt` falls in.
 *
 * The api buckets the 1099 ledger on `YEAR(paid_at)` in the MySQL session
 * timezone, and prod writes `paid_at` in UTC — so this reads the UTC year, not
 * the operator's local one. A Dec 31 / Jan 1 payout genuinely lands in a
 * different year for the two, and quietly showing the local one would tell the
 * operator the opposite of what the ledger will do. (Whether UTC is the right
 * reporting timezone at all is an open question for Finance.)
 */
export function taxYearOf(date: Date): number {
  return date.getUTCFullYear();
}

/** "3 days" — how long a row has been sitting. Age, not a timestamp. */
export function formatAge(iso: string, now: Date = new Date()): string {
  const diffMs = now.getTime() - new Date(iso).getTime();
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return plural(mins, 'minute');
  const hours = Math.floor(mins / 60);
  if (hours < 24) return plural(hours, 'hour');
  return plural(Math.floor(hours / 24), 'day');
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/**
 * How long until a `processing` row may be force-resolved.
 *
 * The cutoff is NOT exposed over GraphQL, so this mirrors the api's
 * `EXPERT_PAYMENT_STALE_AFTER` default of one hour as a client constant. It is
 * a countdown for the operator, never the gate: the server re-checks and returns
 * `expert_payment.too_fresh` regardless of what this says. If the api's tuning
 * changes, this goes stale in the harmless direction — the button fails with a
 * real message rather than moving money early.
 *
 * Anchored on `updatedAt`, which the claim CAS bumps — the same field the api
 * measures from.
 */
export const STALE_AFTER_MS = 60 * 60 * 1000;

export function resolveAvailableAt(updatedAt: string): Date {
  return new Date(new Date(updatedAt).getTime() + STALE_AFTER_MS);
}

export function resolveWait(updatedAt: string, now: Date = new Date()): string | null {
  const available = resolveAvailableAt(updatedAt);
  const remainingMs = available.getTime() - now.getTime();
  if (remainingMs <= 0) return null;
  const mins = Math.max(1, Math.ceil(remainingMs / 60000));
  return mins < 60 ? plural(mins, 'minute') : plural(Math.ceil(mins / 60), 'hour');
}
